import React, {
  useMemo,
} from "react";
import { timeToMinutes, toTwo } from "./utils";
import { ROW_HEIGHT } from "./constants";
import {
  generateAvailableSlots,
  type GeneratedSlot,
} from "./slotGenerationHelpers";

interface WorkSchedule {
  id: string | number;
  psychologistId: number | string;
  weekday: number;
  startTime: string;
  endTime: string;
  active: boolean;
}

interface Session {
  id: string;
  psychologistId: number | string;
  date: string;
  time: string;
  status: string;
  duration?: number;
}

interface MergedRange {
  weekday: number;
  startTime: string;
  endTime: string;
  startMin: number;
  endMin: number;
  dateStr: string;
}

interface Props {
  template: WorkSchedule[];
  sessions: Session[];
  psychologistId: number | string;
  visibleTimeSlots: string[];
  gridRef: React.RefObject<HTMLDivElement>;
  startOfWeek: Date;
  selectedTimeZone: string;
  now: Date;
  slotDuration?: number;
  isSchedulingMode?: boolean;
}

const AvailableRangeOverlay: React.FC<Props> = ({
  template,
  sessions: _sessions,
  psychologistId: _psychologistId,
  visibleTimeSlots,
  startOfWeek,
  selectedTimeZone,
  now,
  slotDuration = 60,
  isSchedulingMode = false,
}) => {
  const remToPx = (rem: number): number => {
    const fontSize = parseFloat(
      getComputedStyle(document.documentElement).fontSize
    );
    return rem * fontSize;
  };

  const ROW_HEIGHT_PX = remToPx(ROW_HEIGHT);

  const sessionsByDate = useMemo(() => {
    const map = new Map<string, Array<{ startMin: number; endMin: number }>>();
    (_sessions || []).forEach((s) => {
      if (!s || s.psychologistId !== _psychologistId) return;
      if (
        !(
          s.status === "active" ||
          s.status === "settled" ||
          s.status === "incomplete" ||
          s.status === "blocked" ||
          s.status === "scheduled"
        )
      )
        return;
      const startMin = timeToMinutes(s.time);
      const sessionDuration = s.duration || 60;
      const endMin = startMin + sessionDuration;
      const arr = map.get(s.date) || [];
      arr.push({ startMin, endMin });
      map.set(s.date, arr);
    });
    return map;
  }, [_sessions, _psychologistId]);

  const filterSlotsAgainstSessions = (
    dateStr: string,
    slots: GeneratedSlot[]
  ): GeneratedSlot[] => {
    const daySessions = sessionsByDate.get(dateStr) || [];
    if (daySessions.length === 0) return slots;
    return slots.filter((slot) => {
      return !daySessions.some(
        ({ startMin, endMin }) =>
          startMin < slot.endMin && endMin > slot.startMin
      );
    });
  };

  const mergedRanges = useMemo(() => {
    const result: MergedRange[] = [];

    for (let weekday = 0; weekday < 7; weekday++) {
      const dayDate = new Date(startOfWeek);
      dayDate.setDate(startOfWeek.getDate() + weekday);
      dayDate.setHours(0, 0, 0, 0);

      const yyyy = dayDate.getFullYear();
      const mm = toTwo(dayDate.getMonth() + 1);
      const dd = toTwo(dayDate.getDate());
      const dateStr = `${yyyy}-${mm}-${dd}`;

      const dayRanges = template
        .filter((t) => t.active && t.weekday === weekday)
        .map((t) => {
          const start = timeToMinutes(t.startTime);
          let end = timeToMinutes(t.endTime);
          if (end === 0 && t.endTime === "00:00") {
            end = 1440;
          }
          return { start, end };
        })
        .sort((a, b) => a.start - b.start);

      if (dayRanges.length === 0) continue;

      const merged: Array<{ start: number; end: number }> = [];

      for (const range of dayRanges) {
        if (merged.length === 0) {
          merged.push({ start: range.start, end: range.end });
        } else {
          const last = merged[merged.length - 1];
          if (range.start <= last.end) {
            last.end = Math.max(last.end, range.end);
          } else {
            merged.push({ start: range.start, end: range.end });
          }
        }
      }

      for (const m of merged) {
        const endTime =
          m.end === 1440
            ? "00:00"
            : `${toTwo(Math.floor(m.end / 60))}:${toTwo(m.end % 60)}`;

        result.push({
          weekday,
          startTime: `${toTwo(Math.floor(m.start / 60))}:${toTwo(
            m.start % 60
          )}`,
          endTime,
          startMin: m.start,
          endMin: m.end,
          dateStr,
        });
      }
    }

    return result;
  }, [template, startOfWeek]);

  if (visibleTimeSlots.length === 0) return null;
  const firstVisibleMin = timeToMinutes(visibleTimeSlots[0]);
  const lastVisibleMin =
    timeToMinutes(visibleTimeSlots[visibleTimeSlots.length - 1]) + 60;

  // Compute open ranges (complement of mergedRanges within visible window)
  const openRanges = (() => {
    if (!isSchedulingMode) return [] as (MergedRange & { isOpen: true })[];
    const result: (MergedRange & { isOpen: true })[] = [];
    const tomorrow = new Date();
    tomorrow.setHours(0, 0, 0, 0);
    tomorrow.setDate(tomorrow.getDate() + 1);

    for (let weekday = 0; weekday < 7; weekday++) {
      const dayDate = new Date(startOfWeek);
      dayDate.setDate(startOfWeek.getDate() + weekday);
      dayDate.setHours(0, 0, 0, 0);
      if (dayDate < tomorrow) continue;

      const dateStr = `${dayDate.getFullYear()}-${toTwo(dayDate.getMonth() + 1)}-${toTwo(dayDate.getDate())}`;

      const dayMerged = mergedRanges
        .filter((r) => r.weekday === weekday)
        .sort((a, b) => a.startMin - b.startMin);

      const visStart = firstVisibleMin;
      const visEnd = lastVisibleMin;

      let cursor = visStart;
      for (const r of dayMerged) {
        if (r.startMin > cursor) {
          const gapStart = Math.max(cursor, visStart);
          const gapEnd = Math.min(r.startMin, visEnd);
          if (gapEnd - gapStart >= slotDuration) {
            result.push({
              weekday,
              dateStr,
              startMin: gapStart,
              endMin: gapEnd,
              startTime: `${toTwo(Math.floor(gapStart / 60))}:${toTwo(gapStart % 60)}`,
              endTime: `${toTwo(Math.floor(gapEnd / 60))}:${toTwo(gapEnd % 60)}`,
              isOpen: true,
            });
          }
        }
        cursor = Math.max(cursor, r.endMin);
      }
      if (cursor < visEnd && visEnd - cursor >= slotDuration) {
        result.push({
          weekday,
          dateStr,
          startMin: cursor,
          endMin: visEnd,
          startTime: `${toTwo(Math.floor(cursor / 60))}:${toTwo(cursor % 60)}`,
          endTime: `${toTwo(Math.floor(visEnd / 60))}:${toTwo(visEnd % 60)}`,
          isOpen: true,
        });
      }
    }
    return result;
  })();

  // Combine work-schedule and open ranges for rendering
  const allRanges = [
    ...mergedRanges.map((r) => ({ ...r, isOpen: false as const })),
    ...openRanges,
  ];

  return (
    <>
      {allRanges.map((range) => {

        // Check if range has any valid slots (skip fully-booked ranges)
        const daySessions = sessionsByDate.get(range.dateStr) || [];
        const availableSubRanges: Array<{ startMin: number; endMin: number }> =
          [];

        if (daySessions.length === 0) {
          availableSubRanges.push({
            startMin: range.startMin,
            endMin: range.endMin,
          });
        } else {
          const sortedSessions = [...daySessions].sort(
            (a, b) => a.startMin - b.startMin
          );
          let currentStart = range.startMin;

          for (const session of sortedSessions) {
            if (session.startMin > currentStart) {
              availableSubRanges.push({
                startMin: currentStart,
                endMin: session.startMin,
              });
            }
            currentStart = Math.max(currentStart, session.endMin);
          }

          if (currentStart < range.endMin) {
            availableSubRanges.push({
              startMin: currentStart,
              endMin: range.endMin,
            });
          }
        }

        // Verify if there are available slots in ANY sub-range
        let hasAnyValidSlot = false;
        for (const subRange of availableSubRanges) {
          if (hasAnyValidSlot) break;

          const subRangeDuration = subRange.endMin - subRange.startMin;
          if (subRangeDuration < slotDuration) {
            continue;
          }
          const maxPossibleOffsets =
            Math.ceil((subRangeDuration - slotDuration) / 30) + 1;
          for (
            let offset = 0;
            offset < maxPossibleOffsets && !hasAnyValidSlot;
            offset++
          ) {
            const tryStart = subRange.startMin + offset * 30;

            if (
              tryStart < range.startMin ||
              tryStart > range.endMin - slotDuration
            ) {
              continue;
            }
            if (
              tryStart < subRange.startMin ||
              tryStart + slotDuration > subRange.endMin
            ) {
              continue;
            }

            const gen = generateAvailableSlots(
              tryStart,
              range.endMin,
              range.dateStr,
              selectedTimeZone,
              now,
              slotDuration
            );
            const filt = filterSlotsAgainstSessions(range.dateStr, gen);
            const validSlots = filt.filter(
              (s) => s.endMin - s.startMin === slotDuration
            );

            if (validSlots.length > 0) {
              hasAnyValidSlot = true;
            }
          }
        }

        if (!hasAnyValidSlot) {
          return null;
        }

        // Calculate position
        const visibleStart = Math.max(range.startMin, firstVisibleMin);
        const visibleEnd = Math.min(range.endMin, lastVisibleMin);
        const visibleDuration = visibleEnd - visibleStart;

        if (visibleDuration <= 0) {
          return null;
        }
        const heightPx = (visibleDuration / 60) * ROW_HEIGHT_PX;

        if (heightPx < 20) return null;

        return null;
      })}
    </>
  );
};

export default AvailableRangeOverlay;
